import React, { useEffect, useRef } from 'react';

import styles from './Table.module.scss';
import { useTable, usePagination } from 'react-table';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import clx from 'classnames';
import { useTranslation } from 'react-i18next';

const splitTableProps = (props = {}) => {
  const { key, ...rest } = props;
  return { key, rest };
};

const Table = ({
  columns = [],
  data = [],
  serverSidePagination = false,
  setPaginationValue,
  pageCount,
  showPageValue = true,
  emptyTableMessage: emptyTableMessageProp,
  entityId,
  isMobile,
  pageRows = 20,
  /** 'top' | 'bottom' | 'both' */
  paginationPosition = 'top',
}) => {
  const rootRef = useRef(null);
  const skipScrollRef = useRef(true);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    pageOptions,
    prepareRow,
    state: { pageIndex, pageSize },
    canPreviousPage,
    canNextPage,
    page,
    nextPage,
    previousPage,
    gotoPage,
    footerGroups,
  } = useTable(
    {
      columns,
      data,
      manualPagination: serverSidePagination,
      ...(serverSidePagination && { pageCount }),
      ...(pageRows && {
        initialState: {
          pageIndex: 0,
          pageSize: pageRows,
        },
      }),
    },
    usePagination,
  );
  const { t } = useTranslation();
  const emptyTableMessage = emptyTableMessageProp || t('common.emptyTableMessage');
  const totalPages = pageOptions.length;
  const showPagination = totalPages > 1;
  const showTopPagination =
    showPagination && (paginationPosition === 'top' || paginationPosition === 'both');
  const showBottomPagination =
    showPagination && (paginationPosition === 'bottom' || paginationPosition === 'both');

  const hasFooterContent = footerGroups.some((group) =>
    group.headers.some((column) => {
      const Footer = column.Footer;
      return typeof Footer === 'function' || (Footer != null && Footer !== '');
    }),
  );

  useEffect(() => {
    if (serverSidePagination && typeof setPaginationValue === 'function') {
      setPaginationValue({
        skip: pageRows * pageIndex,
        limit: pageRows,
      });
    }
  }, [serverSidePagination, pageIndex, pageSize, pageRows, setPaginationValue]);

  useEffect(() => {
    gotoPage(0);
    // Reset scroll skip so filter/search change doesn't force a jump mid-interaction.
    skipScrollRef.current = true;
  }, [entityId]);

  useEffect(() => {
    if (skipScrollRef.current) {
      skipScrollRef.current = false;
      return;
    }
    const node = rootRef.current;
    if (!node) return;

    const scrollParent = node.closest('[class*="section__wrapper"]') || null;
    if (scrollParent) {
      const parentRect = scrollParent.getBoundingClientRect();
      const nodeRect = node.getBoundingClientRect();
      const nextTop = scrollParent.scrollTop + (nodeRect.top - parentRect.top) - 12;
      scrollParent.scrollTo({ top: Math.max(0, nextTop), behavior: 'smooth' });
      return;
    }

    node.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [pageIndex]);

  const handlePrevious = () => {
    if (!canPreviousPage) return;
    previousPage();
  };

  const handleNext = () => {
    if (!canNextPage) return;
    nextPage();
  };

  const renderPagination = (position) => (
    <div
      className={clx(styles.pagination_wrapper, {
        [styles.pagination_wrapper_mobile]: isMobile,
        [styles.pagination_wrapper_top]: position === 'top',
        [styles.pagination_wrapper_bottom]: position === 'bottom',
      })}
    >
      <div className={styles.pagination_meta}>
        {showPageValue ? (
          <>
            <span className={styles.page_eyebrow}>{t('common.tables.paginationLabel')}</span>
            <span className={styles.page_count}>
              {t('common.tables.page', { current: pageIndex + 1, total: totalPages })}
            </span>
          </>
        ) : null}
      </div>

      <div
        className={styles.pagination_controls}
        role="navigation"
        aria-label={t('common.tables.paginationLabel')}
      >
        <button
          type="button"
          className={styles.page_button}
          onClick={handlePrevious}
          disabled={!canPreviousPage}
          aria-label={t('common.tables.prevPage')}
        >
          <ChevronLeft size={18} strokeWidth={2.4} aria-hidden="true" />
          {!isMobile ? <span>{t('common.tables.prevPage')}</span> : null}
        </button>

        <div className={styles.page_indicator} aria-live="polite">
          <span className={styles.page_indicator_current}>{pageIndex + 1}</span>
          <span className={styles.page_indicator_sep}>/</span>
          <span className={styles.page_indicator_total}>{totalPages}</span>
        </div>

        <button
          type="button"
          className={styles.page_button}
          onClick={handleNext}
          disabled={!canNextPage}
          aria-label={t('common.tables.nextPage')}
        >
          {!isMobile ? <span>{t('common.tables.nextPage')}</span> : null}
          <ChevronRight size={18} strokeWidth={2.4} aria-hidden="true" />
        </button>
      </div>
    </div>
  );

  return (
    <div ref={rootRef} className={styles.table_root}>
      {showTopPagination ? renderPagination('top') : null}
      <table
        {...getTableProps()}
        className={clx(styles.table_container, {
          [styles.table_container_mobile]: isMobile,
        })}
      >
        <thead align="left">
          {headerGroups
            .filter(({ headers }) => headers?.some(({ Header }) => !!Header))
            .map((headerGroup) => {
              const { key: headerGroupKey, rest: headerGroupProps } = splitTableProps(
                headerGroup.getHeaderGroupProps(),
              );
              return (
                <tr key={headerGroupKey} {...headerGroupProps}>
                  {headerGroup.headers.map((column) => {
                    const { key: columnKey, rest: columnProps } = splitTableProps(
                      column.getHeaderProps(),
                    );
                    return headerGroups.some((group) =>
                      group.headers.some((col) => !!col.render('Header')),
                    ) ? (
                      <th key={columnKey} {...columnProps} className={styles.table_header}>
                        {column.render('Header')}
                      </th>
                    ) : (
                      <th
                        key={columnKey}
                        {...columnProps}
                        className={styles.table_header}
                        style={{
                          padding: '0px',
                        }}
                      />
                    );
                  })}
                </tr>
              );
            })}
        </thead>
        <tbody {...getTableBodyProps()}>
          {page.length === 0 && (
            <tr>
              <td className={styles.empty_table}>{emptyTableMessage}</td>
            </tr>
          )}
          {page.map((row) => {
            prepareRow(row);
            const { key: rowKey, rest: rowProps } = splitTableProps(row.getRowProps());
            return (
              <tr key={rowKey} {...rowProps}>
                {row.cells.map((cell) => {
                  const { key: cellKey, rest: cellProps } = splitTableProps(cell.getCellProps());
                  return (
                    <td key={cellKey} className={styles.table_cell} {...cellProps}>
                      {cell.render('Cell')}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
        {hasFooterContent ? (
          <tfoot>
            {footerGroups.map((group) => {
              const { key: footerGroupKey, rest: footerGroupProps } = splitTableProps(
                group.getFooterGroupProps(),
              );
              return (
                <tr key={footerGroupKey} {...footerGroupProps}>
                  {group.headers.map((column) => {
                    const { key: footerKey, rest: footerProps } = splitTableProps(
                      column.getFooterProps(),
                    );
                    return (
                      <td key={footerKey} {...footerProps}>
                        {column.render('Footer')}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tfoot>
        ) : null}
      </table>
      {showBottomPagination ? renderPagination('bottom') : null}
    </div>
  );
};

export default Table;
